import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import { useCreateMapPoi, useUpdateMapPoi, useGetMapConfig, useGeocodeAddress } from '../services/appService';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

function MapPoiFormModal({ open, onClose, onSave, poi, appId }) {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        latitude: '',
        longitude: '',
        category: '',
        address: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mapFilters, setMapFilters] = useState([]);
    const [configLoading, setConfigLoading] = useState(false);
    const [configError, setConfigError] = useState('');
    const { geocode, loading: geocodeLoading, error: geocodeError } = useGeocodeAddress();

    const createPoiHook = useCreateMapPoi();
    const updatePoiHook = useUpdateMapPoi();
    const getMapConfigHook = useGetMapConfig();

    useEffect(() => {
        const fetchMapConfig = async () => {
            if (open && appId) {
                setConfigLoading(true);
                setConfigError('');
                try {
                    const config = await getMapConfigHook(appId);
                    if (config && Array.isArray(config.map_filters) && config.map_filters.length > 0) {
                        setMapFilters(config.map_filters);
                    } else {
                        setMapFilters(['Default']);
                    }
                } catch (err) {
                    console.error('Error fetching map config:', err);
                    setConfigError('Could not load category options. Using default values.');
                    setMapFilters(['Default']);
                } finally {
                    setConfigLoading(false);
                }
            }
        };
        
        fetchMapConfig();
    }, [open, appId, getMapConfigHook]);

    useEffect(() => {
        if (poi) {
            setFormData({
                title: poi.title || '',
                description: poi.description || '',
                latitude: poi.latitude !== null && poi.latitude !== undefined ? String(poi.latitude) : '',
                longitude: poi.longitude !== null && poi.longitude !== undefined ? String(poi.longitude) : '',
                category: poi.category || '',
                address: poi.address || '',
            });
        } else {
            setFormData({
                title: '',
                description: '',
                latitude: '',
                longitude: '',
                category: mapFilters && mapFilters.length > 0 ? mapFilters[0] : '',
                address: '',
            });
        }
        setError('');
    }, [poi, open, mapFilters]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleGeocode = useCallback(async () => {
        if (!formData.address) {
            setError('Please enter an address to geocode.');
            return;
        }
        setError('');
        try {
            const coords = await geocode(formData.address);
            if (coords) {
                setFormData(prev => ({
                    ...prev,
                    latitude: String(coords.lat),
                    longitude: String(coords.lon),
                }));
            }
        } catch (err) {
            setError(`Geocoding failed: ${err.message || 'Could not fetch coordinates.'}`);
            setFormData(prev => ({ ...prev, latitude: '', longitude: '' }));
        }
    }, [formData.address, geocode]);

    const validateCoordinates = (latStr, lonStr) => {
        const lat = parseFloat(latStr);
        const lon = parseFloat(lonStr);
        if (isNaN(lat) || isNaN(lon)) {
            return { valid: false, error: 'Latitude and Longitude must be valid numbers.' };
        }
        if (lat < -90 || lat > 90) {
            return { valid: false, error: 'Latitude must be between -90 and 90.' };
        }
        if (lon < -180 || lon > 180) {
            return { valid: false, error: 'Longitude must be between -180 and 180.' };
        }
        return { valid: true, lat, lon };
    }

    const handleSubmit = async () => {
        setError('');
        if (geocodeError) {
             setError(`Please resolve the geocoding error: ${geocodeError}`);
             setLoading(false);
             return;
        }

        setLoading(true);

        const { valid, error: validationError, lat, lon } = validateCoordinates(formData.latitude, formData.longitude);

        if (!valid) {
            setError(validationError);
            setLoading(false);
            return;
        }

        if (!formData.title || !formData.category) {
             setError('Title and Category are required fields.');
             setLoading(false);
             return;
        }

        const payload = {
            title: formData.title,
            description: formData.description || null,
            latitude: lat,
            longitude: lon,
            category: formData.category,
        };

        try {
            if (poi && poi.id) {
                await updatePoiHook(appId, poi.id, payload);
            } else {
                await createPoiHook(appId, payload);
            }
            onSave();
        } catch (err) {
            console.error('Error saving POI:', err);
            setError(err.message || 'Failed to save POI.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{poi ? 'Edit POI' : 'Create New POI'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {geocodeError && !error && <Alert severity="warning" sx={{ mb: 2 }}>Geocoding Error: {geocodeError}</Alert>}
                {configError && <Alert severity="warning" sx={{ mb: 2 }}>{configError}</Alert>}
                
                <TextField
                    autoFocus
                    margin="dense"
                    name="title"
                    label="Title"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={formData.title}
                    onChange={handleChange}
                    required
                />
                <TextField
                    margin="dense"
                    name="description"
                    label="Description"
                    type="text"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={formData.description}
                    onChange={handleChange}
                />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 1 }}>
                   <TextField
                       margin="dense"
                       name="address"
                       label="Address (for Geocoding)"
                       type="text"
                       fullWidth
                       variant="outlined"
                       value={formData.address}
                       onChange={handleChange}
                       sx={{ flexGrow: 1 }}
                   />
                   <Button
                       onClick={handleGeocode}
                       variant="outlined"
                       disabled={geocodeLoading || !formData.address}
                       size="medium"
                       sx={{ height: '56px', whiteSpace: 'nowrap' }}
                   >
                       {geocodeLoading ? <CircularProgress size={24} /> : 'Fetch Coords'}
                   </Button>
                </Stack>

                <Grid container spacing={2}>
                     <Grid item xs={12} sm={6}>
                         <TextField
                             margin="dense"
                             name="latitude"
                             label="Latitude"
                             type="number"
                             fullWidth
                             variant="outlined"
                             value={formData.latitude}
                             onChange={handleChange}
                             required
                             inputProps={{ step: "any" }}
                         />
                     </Grid>
                     <Grid item xs={12} sm={6}>
                         <TextField
                             margin="dense"
                             name="longitude"
                             label="Longitude"
                             type="number"
                             fullWidth
                             variant="outlined"
                             value={formData.longitude}
                             onChange={handleChange}
                             required
                             inputProps={{ step: "any" }}
                         />
                     </Grid>
                </Grid>

                <FormControl fullWidth margin="dense" required>
                    <InputLabel id="category-select-label">Category</InputLabel>
                    <Select
                        labelId="category-select-label"
                        id="category-select"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        label="Category"
                        disabled={configLoading || mapFilters.length === 0}
                    >
                        {configLoading ? (
                            <MenuItem value="" disabled>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    <Typography>Loading categories...</Typography>
                                </Box>
                            </MenuItem>
                        ) : (
                            mapFilters.map((filter) => (
                                <MenuItem key={filter} value={filter}>
                                    {filter}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading || geocodeLoading}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading || geocodeLoading || configLoading}>
                    {(loading || geocodeLoading) ? <CircularProgress size={24} /> : (poi ? 'Save Changes' : 'Create POI')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MapPoiFormModal; 